// KPI Table consts
import React from 'react';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import forIn from 'lodash/forIn';
import set from 'lodash/set';
import get from 'lodash/get';

import {
  LABEL_KEY, LABEL_COLUMN, COLUMN_SPLIT_CHAR_SEQUENCE,
} from './constants';
import {
  KPI_REPORT_PICK_VOLUME,
  KPI_COST_SUMMARY,
  KPI_UTILISATION_REPORT,
  KPI_FACILITY_REPORT,
  KPI_STORAGE_REPORT,
  KPI_PRODUCTIVITY_REPORT,
} from '../../../plannerConfig';

// Apply diff rendering if the value is an object, otherwise just display the number
const diffRenderer = config => (value) => {
  const style = {};
  // If the color specified is not null, apply it
  if (get(value, 'color')) {
    style.color = value.color;
  }
  const cellValue = get(value, 'value', value);

  // eslint-disable-next-line no-restricted-globals
  const humanisedValue = isNaN(cellValue)
    ? cellValue
    : (config.applyRounding ? Math.floor(cellValue) : cellValue).toLocaleString();
  return <span style={style}>{humanisedValue}</span>;
};

// Gets unique column headers with numerical values (i.e removes label column)
const getColumnHeaders = reportData => reportData
  .reduce((a, c) => {
    const recordWithoutLabel = omit(c, LABEL_KEY);
    return uniq([...a, ...Object.keys(recordWithoutLabel)]);
  }, []);

// Recursively adds titles from a provided nested object of columns.
// At each level we iterate through the property's set, set the column's title
// to be the properties key, and do the same for any children the property has.
const addTitle = (collection) => {
  const collectionCopy = { ...collection };
  forIn(collectionCopy, (value, key) => {
    collectionCopy[key].title = key;
    if (value.children) {
      collectionCopy[key].children = addTitle(value.children);
    }
  });
  return Object.values(collectionCopy);
};


/* Factory for generating the grouped column layout tables in the report.
   The gist of it is that the backend will send through a flat array of columns. Each
   column can be delimited to indicate that it should be nested, eg
   [volume_pick_plan, volume_pick_reop] should display as plan and reop nested under pick
   nested under volume.

   The way we achieve this is iterating through the unique column headers,
   casting each string to a column object. We create a path for the object by splitting
   on the delimiter, and use it with lodashes set fn to deeply set it within the column
   collection obj.

   This gets us the majority of the way there, but we also need to get each columns title
   and convert the collection object to an array. To that end we use the addTitle fn
   to work through the collection object, adding titles, calling recursively
   where a children property is present, and finally returning
   us a well formed array the AntD table can ingest.

   Some cells represent a difference and have formatting applied. Some values should be
   green if their diff is positive, and others should be red for a positive diff. The
   backend sends this configuration by sending an object for the cell value that encodes
   this colouring information.
 */
const defaultStackedColumnsConfig = { applyRounding: true };
const getStackedColumns = (config = defaultStackedColumnsConfig) => (data) => {
  const columnHeaders = getColumnHeaders(data);
  const collection = {};
  columnHeaders.forEach((columnPathArray) => {
    const path = columnPathArray.split(COLUMN_SPLIT_CHAR_SEQUENCE);
    const columnDefinition = {
      key: columnPathArray,
      dataIndex: columnPathArray,
      render: diffRenderer(config),
    };
    set(collection, path.join('.children.'), columnDefinition);
  });
  return [LABEL_COLUMN, ...addTitle(collection)];
};

export const REPORT_TABLES = [{
  key: KPI_REPORT_PICK_VOLUME,
  title: 'Pick Volume',
  getColumns: getStackedColumns(),
  width: '100%',
}, {
  key: KPI_COST_SUMMARY,
  title: 'Cost Summary',
  getColumns: getStackedColumns({ applyRounding: false }),
  width: '25%',
}, {
  key: KPI_UTILISATION_REPORT,
  title: 'Utilisation Report',
  getColumns: getStackedColumns(),
  width: '100%',
}, {
  key: KPI_FACILITY_REPORT,
  title: 'Facility Report',
  getColumns: getStackedColumns(),
  width: '33.33%',
}, {
  key: KPI_STORAGE_REPORT,
  title: 'Storage Report',
  getColumns: getStackedColumns(),
  width: '33.33%',
}, {
  key: KPI_PRODUCTIVITY_REPORT,
  title: 'Productivity Report',
  getColumns: getStackedColumns(),
  width: '33.33%',
}];
